import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { Avatar } from './visuals'

export interface Address { name: string; email: string }
interface Suggestion extends Address { sent: number; received: number }

/** "Ana <ana@x.com>, bo@y.com" → addresses. */
export const addresses = (value: string): Address[] => value.split(/[,;]+/).map(part => part.trim()).filter(Boolean).map(part => {
  const match = /^(.*?)\s*<([^>]+)>$/.exec(part)
  return match ? { name: match[1]!.replace(/^"|"$/g, ''), email: match[2]!.trim() } : { name: '', email: part }
})
export const validEmail = (email: string) => /^[^\s<>@,;]+@[^\s<>@,;]+\.[^\s<>@,;]+$/.test(email)
const format = (address: Address) => address.name ? `${address.name.replace(/[<>,;"]/g, '')} <${address.email}>` : address.email
const join = (list: Address[], draft = '') => [...list.map(format), draft].filter(Boolean).join(', ') + (list.length && !draft ? ', ' : '')

let warmed = false

/** Enter on an empty recipient field moves on, like Tab. */
function focusNext(from: HTMLElement) {
  const all = [...document.querySelectorAll<HTMLElement>('input, textarea, select, button, [tabindex]')].filter(el => el.tabIndex >= 0 && !(el as HTMLInputElement).disabled && el.offsetParent !== null)
  all[all.indexOf(from) + 1]?.focus()
}

/**
 * A recipient field that feels native to the keyboard: type a name or address,
 * ↑/↓ to pick from your ranked contacts, Enter to add, Tab to add and move on,
 * comma to add what you typed, Backspace on an empty field to select and then
 * remove the last person. The value stays a plain "Name <email>, …" string.
 */
export const RecipientInput = forwardRef<HTMLInputElement, {
  value: string; onChange: (value: string) => void; placeholder?: string; className?: string; autoFocus?: boolean
}>(function RecipientInput({ value, onChange, placeholder, className = '', autoFocus }, outer) {
  const input = useRef<HTMLInputElement>(null)
  useImperativeHandle(outer, () => input.current!)
  const parts = value.split(/[,;]/)
  const chosen = addresses(parts.slice(0, -1).join(','))
  const draft = parts[parts.length - 1]!.trimStart()
  const [suggestions, setSuggestions] = useState<Suggestion[]>([])
  const [active, setActive] = useState(0)
  const [focused, setFocused] = useState(false)
  const [marked, setMarked] = useState(false)
  const [dismissed, setDismissed] = useState(false)
  const skipBlur = useRef(false)

  useEffect(() => {
    if (!focused || !draft.trim()) { setSuggestions([]); return }
    const controller = new AbortController()
    const timer = setTimeout(async () => {
      try {
        const response = await fetch(`/api/contacts?q=${encodeURIComponent(draft.trim())}`, { signal: controller.signal })
        const { items } = await response.json() as { items: Suggestion[] }
        const taken = new Set(chosen.map(item => item.email.toLowerCase()))
        setSuggestions(items.filter(item => !taken.has(item.email)).slice(0, 6)); setActive(0)
      } catch { /* aborted */ }
    }, 60)
    return () => { clearTimeout(timer); controller.abort() }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, focused])

  const open = focused && !dismissed && suggestions.length > 0 && !!draft.trim()
  const commit = (address: Address) => { onChange(join([...chosen, address])); setSuggestions([]); setDismissed(false) }
  const commitTyped = () => {
    const typed = addresses(draft)[0]
    if (typed) onChange(join([...chosen, typed]))
  }
  const remove = (index: number) => { onChange(join(chosen.filter((_, i) => i !== index), draft)); input.current?.focus() }

  return (
    <div className={`recipients ${className}`} onMouseDown={event => { if (event.target === event.currentTarget) { event.preventDefault(); input.current?.focus() } }}>
      {chosen.map((address, index) => (
        <span key={`${address.email}-${index}`} className={`rcpt${validEmail(address.email) ? '' : ' bad'}${marked && index === chosen.length - 1 ? ' marked' : ''}`} title={address.email}>
          {address.name || address.email}
          <button type="button" tabIndex={-1} onMouseDown={event => event.preventDefault()} onClick={() => remove(index)} aria-label={`Remove ${address.email}`}>×</button>
        </span>
      ))}
      <input ref={input} className="recipients-input" value={draft} placeholder={chosen.length ? '' : placeholder} autoFocus={autoFocus}
        autoComplete="off" spellCheck={false} role="combobox" aria-expanded={open} aria-autocomplete="list"
        onFocus={() => {
          setFocused(true)
          if (!warmed) { warmed = true; void fetch('/api/contacts?warm=1').catch(() => {}) }
        }}
        onBlur={() => { setFocused(false); setMarked(false); if (!skipBlur.current && draft.trim()) commitTyped(); skipBlur.current = false }}
        onChange={event => {
          setMarked(false); setDismissed(false)
          const text = event.target.value
          if (/[,;]$/.test(text)) { const typed = addresses(text)[0]; if (typed) onChange(join([...chosen, typed])); return }
          onChange(join(chosen, text))
        }}
        onKeyDown={event => {
          if (open && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
            event.preventDefault(); event.stopPropagation()
            setActive(index => (index + (event.key === 'ArrowDown' ? 1 : -1) + suggestions.length) % suggestions.length)
            return
          }
          if (event.key === 'Enter' && !event.metaKey && !event.ctrlKey) {
            event.preventDefault()
            if (open) commit(suggestions[active]!); else if (draft.trim()) commitTyped(); else focusNext(event.currentTarget)
            return
          }
          // Tab adds the highlighted person and lets focus move on to the next field.
          if (event.key === 'Tab' && !event.shiftKey && draft.trim()) { skipBlur.current = true; if (open) commit(suggestions[active]!); else commitTyped(); return }
          if (event.key === 'Escape' && open) { event.preventDefault(); event.stopPropagation(); setDismissed(true); return }
          if (event.key === 'Backspace' && !draft && chosen.length) {
            event.preventDefault()
            if (marked) { onChange(join(chosen.slice(0, -1))); setMarked(false) } else setMarked(true)
            return
          }
          setMarked(false)
        }} />
      {open && (
        <div className="suggestions" role="listbox">
          {suggestions.map((item, index) => (
            <div key={item.email} role="option" aria-selected={index === active} className={`suggestion${index === active ? ' on' : ''}`}
              onMouseDown={event => { event.preventDefault(); commit(item) }} onMouseEnter={() => setActive(index)}>
              <Avatar name={item.name} email={item.email} person size={22} />
              <span className="suggestion-name">{item.name || item.email}</span>
              {item.name && <span className="suggestion-email">{item.email}</span>}
              <span className="suggestion-why">{item.sent ? `you wrote ${item.sent}×` : `wrote you ${item.received}×`}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
})
